/* eslint-disable */
/**
 * Decuparea pozei la căutarea după imagine.
 *
 * DE CE: oamenii urcă des o fotografie de interior, dar caută UN produs din ea
 * — tapetul de pe perete, riflajul, un profil. CLIP compară compoziția
 * întreagă, așa că o cameră cu mobilă, plante și fereastră se potrivește cu
 * alte camere, nu cu tapetul din ea. Lăsându-l pe utilizator să izoleze zona,
 * interogarea devine chiar produsul.
 *
 * DECUPAJ PĂTRAT, nu liber. Serverul mai taie o dată, pătrat din centru, în
 * `ModelHelperListing::imageSearchIds()`, fiindcă intrarea modelului e pătrată.
 * O selecție dreptunghiulară ar fi deci retăiată tăcut și omul ar căuta după
 * altceva decât a ales. Pătratul face lanțul onest: ce selectează e exact ce se
 * trimite la model.
 *
 * Ieșirea se limitează la 1024px — modelul lucrează la 224, orice peste asta e
 * doar trafic. Fișierul trimis ESTE decupajul, deci panoul plutitor de pe
 * pagina de rezultate arată zona aleasă, nu poza originală; asta e și ce vrei
 * să compari cu rezultatele.
 *
 * Degradare: dacă lipsește Cropper, DataTransfer sau canvas.toBlob, se trimite
 * poza originală, adică exact comportamentul de dinainte.
 *
 * Etichetele vin din window.SEARCH_CROP_T (injectat în common/header.tpl din
 * fișierele de limbă) — nimic hardcodat aici.
 *
 * CLASELE DE BUTON: tema n-are `dark` în paletă. Bootstrap-ul ei se compilează
 * cu black / cyan / pink / red / white, deci `btn-dark` și `btn-outline-dark`
 * nu produc nicio regulă și butoanele se randează ca text simplu. Folosim
 * aceleași clase ca modalul de decupare a modelului (pattern-cropper.js):
 * btn-black pentru acțiunea principală, btn-gray-heavy-light pentru cea
 * secundară. Verifică în assets/css/bootstrap.min.css înainte de a schimba.
 */

import Cropper from 'cropperjs';
import cropperCss from 'cropperjs/dist/cropper.min.css';

const MODAL_ID  = 'search-image-crop-modal';
const INPUT_ID  = 'search-image-input';
const MAX_SIDE  = 1024;

let cropper    = null;
let modalEl    = null;
let bsModal    = null;
let cssInjected = false;
let objectUrl  = null;
// Cropper.js măsoară imaginea la inițializare. Pornit în `img.onload`, prinde
// modalul în plină tranziție de fade și măsoară o cutie mică — de aici ieșea o
// miniatură. Așteptăm ambele semnale: imaginea încărcată ȘI modalul afișat.
let imgReady    = false;
let modalShown  = false;
// Am pornit deja trimiterea? Handler-ul de închidere golește input-ul ca a doua
// alegere a ACELUIAȘI fișier să mai declanșeze `change`, dar dacă tocmai am pus
// decupajul în el n-are ce goli — `hidden.bs.modal` vine după tranziție, adică
// după ce am chemat submit.
let submitting = false;

function t(key, fallback) {
    const d = window.SEARCH_CROP_T || {};
    return (d[key] !== undefined && d[key] !== '') ? d[key] : (fallback || '');
}

function injectCss() {
    if (cssInjected) return;
    const style = document.createElement('style');
    style.textContent = typeof cropperCss === 'string'
        ? cropperCss
        : (cropperCss && cropperCss.default ? cropperCss.default : '');
    document.head.appendChild(style);

    const own = document.createElement('style');
    own.textContent = `
/* Două coloane: imaginea în stânga, comenzile în dreapta. Butoanele au stat
   întâi într-un modal-footer sub imagine, ceea ce însemna scroll ca să ajungi
   la ele — pe un ecran de laptop, la o poză verticală, butoanele cădeau sub
   marginea de jos. */
#${MODAL_ID} .sic-body { display:flex; gap:20px; align-items:flex-start; justify-content:center; }
/* Fără fundal pe scenă: la o poză verticală, coloana rămâne mai lată decât
   imaginea, iar un fundal gri ar desena benzi de o parte și de alta. Pe alb,
   imaginea centrată arată intenționat. Cropper.js întunecă oricum singur ce e
   în afara selecției, deci nu pierdem conturul zonei. */
#${MODAL_ID} .sic-stage { display:flex; justify-content:center; flex:0 0 auto; min-width:0; }
/* Cutia se mulează pe imagine: fără înălțime fixă, doar un plafon pe imagine.
   O înălțime fixă lăsa benzi goale sub și peste o poză orizontală. Dimensiunea
   mare vine din plafon plus inițializarea de după shown.bs.modal; aia era
   cauza miniaturii, nu lipsa înălțimii. */
#${MODAL_ID} .sic-stage img { display:block; max-width:100%; }
#${MODAL_ID} .sic-side { flex:0 0 260px; display:flex; flex-direction:column; gap:10px; }
#${MODAL_ID} .cropper-container { margin:0 auto; }
/* Selecția trebuie să se vadă peste orice fotografie, deci contur alb dublat
   cu umbră, nu doar linia albastră implicită a bibliotecii. */
#${MODAL_ID} .cropper-view-box { outline:2px solid #fff; box-shadow:0 0 0 1px rgba(0,0,0,.5); }
#${MODAL_ID} .cropper-point { background:#fff; width:10px; height:10px; opacity:1; }
#${MODAL_ID} .cropper-modal { opacity:.6; }
/* .btn-link al temei are --bs-btn-color: var(--bs-white) — gândit pentru
   fundal închis. Pe fundalul alb al modalului, „Resetează" ieșea alb pe alb,
   adică invizibil. Suprascriem variabila, nu culoarea: altfel regula .btn
   (color: var(--bs-btn-color)) ar câștiga oricum. */
#${MODAL_ID} .sic-reset {
    --bs-btn-color: #000;
    --bs-btn-hover-color: var(--bs-pink, #d6336c);
}
@media (max-width: 767.98px) {
    /* Sub tabletă coloanele se suprapun, deci trecem pe verticală — dar tăiem
       și plafonul imaginii, altfel butoanele ajung iar sub marginea ecranului. */
    #${MODAL_ID} .sic-body { flex-direction:column; gap:12px; }
    #${MODAL_ID} .sic-side { flex:1 1 auto; width:100%; }
    /* Pe mobil tăiem plafonul de înălțime, ca butoanele din coloana de sub
       imagine să rămână pe ecran. Valoarea o citește maybeInitCropper. */
    #${MODAL_ID} .sic-stage { --sic-max-vh: 0.46; }
}
`;
    document.head.appendChild(own);
    cssInjected = true;
}

function buildModal() {
    if (modalEl) return modalEl;
    const wrap = document.createElement('div');
    wrap.innerHTML = `
<div class="modal fade" id="${MODAL_ID}" tabindex="-1" aria-hidden="true">
  <div class="modal-dialog modal-xl modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title fs-18 fw-semibold">${t('heading')}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
      </div>
      <div class="modal-body">
        <div class="sic-body">
          <div class="sic-stage"><img alt="" class="sic-image"></div>
          <div class="sic-side">
            <p class="text-gray-pre-dark fs-14 mb-4 sic-help">${t('help')}</p>
            <button type="button" class="btn btn-black fw-semibold w-100 sic-go">${t('confirm')}</button>
            <button type="button" class="btn btn-gray-heavy-light fw-semibold w-100 sic-all">${t('skip')}</button>
            <button type="button" class="btn btn-link btn-sm p-0 text-decoration-underline align-self-start sic-reset">${t('reset')}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`;
    modalEl = wrap.firstElementChild;
    document.body.appendChild(modalEl);
    return modalEl;
}

/** Curăță tot ce ține de o sesiune de decupare. */
function teardown() {
    if (cropper) { cropper.destroy(); cropper = null; }
    if (modalEl) {
        const stage = modalEl.querySelector('.sic-stage');
        // Dimensiunile puse în pixeli la init nu trebuie moștenite de poza
        // următoare — ar fi calculate pentru alt raport.
        if (stage) { stage.style.width = ''; stage.style.height = ''; stage.style.flex = ''; }
    }
    if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; }
}

function submitForm(form) {
    submitting = true;
    // Formularul e trimis nativ; jQuery nu e garantat încărcat aici.
    if (typeof form.requestSubmit === 'function') { form.requestSubmit(); }
    else { form.submit(); }
}

/**
 * Pune blobul decupat în input-ul de fișier, ca serverul să primească un upload
 * normal. Fără DataTransfer nu se poate rescrie `files`, deci cădem pe original.
 */
function replaceFileAndSubmit(input, form, blob, name) {
    try {
        const file = new File([blob], name, { type: 'image/jpeg' });
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
    } catch (e) {
        // Nu putem înlocui fișierul — trimitem originalul.
    }
    submitForm(form);
}

/** Pornește Cropper doar când imaginea e încărcată și modalul complet afișat. */
function maybeInitCropper(img) {
    if (!imgReady || !modalShown || cropper) return;

    // Dimensionăm SCENA, în pixeli, înainte de init.
    //
    // Cropper.js își face containerul după elementul PĂRINTE al imaginii, nu
    // după imagine. Deci degeaba limitezi poza din CSS: containerul rămâne cât
    // coloana, iar `.cropper-modal` (întunecarea din afara selecției) se
    // pictează și peste zona goală de lângă poză — apar benzi gri pe lateral la
    // pozele verticale, exact cele făcute cu telefonul.
    //
    // Scena are flex:0 0 auto, deci lățimea pe care o punem aici e cea finală;
    // o măsurăm întâi cu flex-grow pornit, ca să știm cât spațiu avem.
    const stage = img.parentElement;
    stage.style.flex = '1 1 auto';
    stage.style.width = '';
    stage.style.height = '';
    const availW = Math.max(120, stage.clientWidth);
    const vhVar = parseFloat(getComputedStyle(stage).getPropertyValue('--sic-max-vh'));
    const vhCap = (vhVar > 0 && vhVar <= 1) ? vhVar : 0.66;
    const availH = Math.min(window.innerHeight * vhCap, 640);
    const natW = img.naturalWidth || availW;
    const natH = img.naturalHeight || availH;
    const scale = Math.min(availW / natW, availH / natH, 1);
    stage.style.flex   = '0 0 auto';
    stage.style.width  = Math.round(natW * scale) + 'px';
    stage.style.height = Math.round(natH * scale) + 'px';

    cropper = new Cropper(img, {
        viewMode: 1,
        aspectRatio: 1,          // vezi antetul: serverul taie pătrat oricum
        autoCropArea: 0.5,       // pornim strâns, ca să invite la ajustare
        dragMode: 'move',
        background: false,
        zoomable: true,
        movable: true,
        rotatable: false,
        scalable: false,
        toggleDragModeOnDblclick: false,
    });
}

function openCropper(file, input, form) {
    injectCss();
    buildModal();

    const img = modalEl.querySelector('.sic-image');
    teardown();
    objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    if (!bsModal) {
        const BS = window.bootstrap;
        if (!BS || !BS.Modal) { submitForm(form); return; }
        bsModal = new BS.Modal(modalEl, { backdrop: 'static' });

        modalEl.addEventListener('shown.bs.modal', function () {
            modalShown = true;
            maybeInitCropper(modalEl.querySelector('.sic-image'));
        });
        modalEl.addEventListener('hidden.bs.modal', function () {
            modalShown = false;
            teardown();
            if (submitting) { return; }
            // Închis fără alegere: golim input-ul, ca a doua alegere a
            // aceluiași fișier să declanșeze iar `change`.
            try { input.value = ''; } catch (e) {}
        });
        modalEl.querySelector('.sic-reset').addEventListener('click', function () {
            if (cropper) cropper.reset();
        });
        modalEl.querySelector('.sic-all').addEventListener('click', function () {
            // Fără decupaj: exact fluxul de dinainte.
            bsModal.hide();
            submitForm(form);
        });
        modalEl.querySelector('.sic-go').addEventListener('click', function () {
            if (!cropper) { submitForm(form); return; }
            const canvas = cropper.getCroppedCanvas({
                maxWidth: MAX_SIDE,
                maxHeight: MAX_SIDE,
                imageSmoothingQuality: 'high',
                fillColor: '#fff',
            });
            if (!canvas || typeof canvas.toBlob !== 'function') { submitForm(form); return; }
            const base = (file.name || 'crop').replace(/\.[^.]+$/, '');
            canvas.toBlob(function (blob) {
                bsModal.hide();
                if (!blob) { submitForm(form); return; }
                replaceFileAndSubmit(input, form, blob, base + '-crop.jpg');
            }, 'image/jpeg', 0.92);
        });
    }

    imgReady = false;
    modalShown = false;
    img.onload = function () { imgReady = true; maybeInitCropper(img); };

    bsModal.show();
}

(function install() {
    const input = document.getElementById(INPUT_ID);
    if (!input) return;
    const form = input.form || input.closest('form');
    if (!form) return;

    // Temele care n-au primit încă modificarea de șablon mai au
    // onchange="$(this).parent().submit()" pe input. `app.min.js` e un build
    // comun și se copiază între teme, deci modulul ăsta poate ajunge acolo
    // înaintea șablonului — iar atunci formularul s-ar trimite în paralel cu
    // deschiderea modalului. Îl scoatem, ca ordinea de livrare să nu conteze.
    input.removeAttribute('onchange');
    input.onchange = null;

    input.addEventListener('change', function () {
        const file = input.files && input.files[0];
        if (!file) return;
        submitting = false;
        // Ce nu poate fi decupat (SVG, fișier corupt) merge direct la server.
        if (!/^image\//.test(file.type) || /svg/i.test(file.type)) { submitForm(form); return; }
        try {
            openCropper(file, input, form);
        } catch (e) {
            submitForm(form);
        }
    });
})();
